import { useQuery, useMutation } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Users, FolderTree, Activity, UserCog, ShieldCheck, UserCheck, ArrowRight, RotateCcw, FlaskConical, Skull, Trash2, Bug } from "lucide-react";
import type { AuditLog } from "@shared/schema";
import { format, formatDistanceToNow } from "date-fns";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useState } from "react";
import * as Sentry from "@sentry/react";

export default function AdminOverview() {
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const [resetDemoDialogOpen, setResetDemoDialogOpen] = useState(false);
  const [testDeathDialogOpen, setTestDeathDialogOpen] = useState(false);
  const [testDeathEmail, setTestDeathEmail] = useState("");
  const [testDeathDate, setTestDeathDate] = useState(new Date().toISOString().split("T")[0]);

  const { data: stats } = useQuery<{
    totalUsers: number;
    totalClients: number;
    totalAdvisors: number;
    totalExecutors: number;
    totalAdmins: number;
    totalCategories: number;
    totalChecklistItems: number;
  }>({
    queryKey: ["/api/admin/stats"],
  });

  const { data: recentActivity = [], isLoading: activityLoading } = useQuery<AuditLog[]>({
    queryKey: ["/api/admin/audit-log", 5, 0],
    queryFn: async () => {
      const res = await fetch("/api/admin/audit-log?limit=5&offset=0");
      if (!res.ok) throw new Error("Failed to fetch audit logs");
      return res.json();
    },
  });

  const resetDemoMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/admin/reset-demo");
      return res.json();
    },
    onSuccess: () => {
      toast({ title: "Demo account reset", description: "demo@estateplanner.ca has been restored to factory state." });
      setResetDemoDialogOpen(false);
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to reset demo account", variant: "destructive" });
    },
  });

  const { data: allClients = [] } = useQuery<{ id: string; name: string; email: string }[]>({
    queryKey: ["/api/admin/users"],
    queryFn: async () => {
      const res = await fetch("/api/admin/users?role=client&limit=200");
      if (!res.ok) return [];
      const users = await res.json();
      return users.map((u: any) => ({ id: u.id, name: u.fullName, email: u.email }));
    },
  });

  const createTestDeathMutation = useMutation({
    mutationFn: async ({ clientEmail, dateOfDeath }: { clientEmail: string; dateOfDeath: string }) => {
      const res = await apiRequest("POST", "/api/admin/create-test-death-event", { clientEmail, dateOfDeath });
      return res.json();
    },
    onSuccess: () => {
      toast({ title: "Test death event created", description: "The client's executors can now access the post-death dashboard." });
      setTestDeathDialogOpen(false);
      setTestDeathEmail("");
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to create test death event", variant: "destructive" });
    },
  });

  const clearTestDeathMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("DELETE", "/api/admin/clear-test-death-events");
      return res.json();
    },
    onSuccess: (data: any) => {
      toast({ title: "Cleared", description: `${data.count ?? 0} test death event(s) removed.` });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to clear test death events", variant: "destructive" });
    },
  });

  const statCards = [
    {
      title: "Total Users",
      value: stats?.totalUsers ?? "—",
      icon: Users,
      description: "All system users",
      link: "/users",
    },
    {
      title: "Clients",
      value: stats?.totalClients ?? "—",
      icon: UserCheck,
      description: "Client accounts",
      link: "/users?role=client",
    },
    {
      title: "Advisors",
      value: stats?.totalAdvisors ?? "—",
      icon: UserCog,
      description: "Financial advisors",
      link: "/users?role=advisor",
    },
    {
      title: "Administrators",
      value: stats?.totalAdmins ?? "—",
      icon: ShieldCheck,
      description: "System administrators",
      link: "/users?role=administrator",
    },
    {
      title: "Default Categories",
      value: stats?.totalCategories ?? "—",
      icon: FolderTree,
      description: "Template categories",
      link: "/categories",
    },
  ];

  const getActionVariant = (action: string): "default" | "secondary" | "destructive" | "outline" => {
    if (action.startsWith("CREATE")) return "default";
    if (action.startsWith("UPDATE")) return "secondary";
    if (action.startsWith("DELETE")) return "destructive";
    return "outline";
  };

  const formatAction = (log: AuditLog) => {
    const parts = log.action.split("_");
    const verb = parts[0];
    const subject = parts.slice(1).join(" ").toLowerCase();
    return { verb, subject };
  };

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-3xl font-bold" data-testid="text-page-title">
          System Overview
        </h1>
        <p className="text-muted-foreground mt-2">
          Monitor and manage your estate planning platform
        </p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {statCards.map((stat) => {
          const Icon = stat.icon;
          return (
            <Card
              key={stat.title}
              data-testid={`card-stat-${stat.title.toLowerCase().replace(/\s+/g, "-")}`}
              className="hover-elevate cursor-pointer transition-all"
              onClick={() => setLocation(stat.link)}
            >
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  {stat.title}
                </CardTitle>
                <div className="flex items-center justify-center h-8 w-8 rounded-md bg-primary/10">
                  <Icon className="h-4 w-4 text-primary" />
                </div>
              </CardHeader>
              <CardContent>
                <div
                  className="text-3xl font-bold"
                  data-testid={`text-stat-${stat.title.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {stat.value}
                </div>
                <p className="text-xs text-muted-foreground mt-1">{stat.description}</p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Card data-testid="card-demo-tools">
        <CardHeader className="flex flex-row items-center gap-3 pb-3">
          <FlaskConical className="h-5 w-5 text-muted-foreground" />
          <CardTitle className="text-base">Demo Tools</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium">demo@estateplanner.ca</p>
              <p className="text-xs text-muted-foreground mt-0.5">Clears all estate plan data and resets the onboarding wizard</p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setResetDemoDialogOpen(true)}
              data-testid="button-reset-demo"
            >
              <RotateCcw className="h-4 w-4 mr-2" />
              Reset Demo Account
            </Button>
          </div>
          <div className="flex items-center justify-between border-t pt-3">
            <div>
              <p className="text-sm font-medium">Sentry Error Tracking</p>
              <p className="text-xs text-muted-foreground mt-0.5">Sends a test event directly to Sentry via captureException. Check dashboard ~1 min after clicking. No-op in development (Sentry disabled).</p>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                Sentry.captureException(
                  new Error(
                    "Deliberate test error — if you see this in Sentry, tracking works. Fake PII in payload to verify scrubbing: email=fake@example.com, ssn=000-00-0000"
                  )
                );
                toast({ title: "Sentry test fired", description: "Check your Sentry dashboard in ~1 minute." });
              }}
              data-testid="button-trigger-sentry-error"
            >
              <Bug className="h-4 w-4 mr-2" />
              Trigger Test Sentry Error
            </Button>
          </div>
          <div className="flex items-center justify-between border-t pt-3">
            <div>
              <p className="text-sm font-medium">Create Test Death Event</p>
              <p className="text-xs text-muted-foreground mt-0.5">Simulate a verified death to test the executor post-death dashboard and letters</p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => clearTestDeathMutation.mutate()}
                disabled={clearTestDeathMutation.isPending}
                data-testid="button-clear-test-deaths"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Clear Test Events
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setTestDeathDialogOpen(true)}
                data-testid="button-create-test-death"
              >
                <Skull className="h-4 w-4 mr-2" />
                Create Test Death Event
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card data-testid="card-recent-activity">
        <CardHeader className="flex flex-row items-center justify-between">
          <div className="flex items-center gap-3">
            <Activity className="h-5 w-5 text-muted-foreground" />
            <CardTitle>Recent Activity</CardTitle>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setLocation("/audit-log")}
            className="gap-1 text-muted-foreground"
            data-testid="button-view-all-activity"
          >
            View all
            <ArrowRight className="h-3.5 w-3.5" />
          </Button>
        </CardHeader>
        <CardContent>
          {activityLoading ? (
            <div className="text-sm text-muted-foreground py-4 text-center">Loading...</div>
          ) : recentActivity.length === 0 ? (
            <div className="text-sm text-muted-foreground py-4 text-center">
              No activity recorded yet.
            </div>
          ) : (
            <div className="space-y-3">
              {recentActivity.map((log) => {
                const { subject } = formatAction(log);
                const createdAt = new Date(log.createdAt);
                return (
                  <div
                    key={log.id}
                    className="flex items-start justify-between gap-3 py-2 border-b last:border-0"
                    data-testid={`activity-row-${log.id}`}
                  >
                    <div className="flex items-start gap-3 min-w-0">
                      <Badge
                        variant={getActionVariant(log.action)}
                        className="mt-0.5 shrink-0 text-xs"
                      >
                        {log.action}
                      </Badge>
                      <div className="min-w-0">
                        {subject && (
                          <p className="text-sm text-muted-foreground capitalize truncate">
                            {subject}
                            {log.targetTable && (
                              <span className="font-mono text-xs ml-1 opacity-60">
                                [{log.targetTable}]
                              </span>
                            )}
                          </p>
                        )}
                      </div>
                    </div>
                    <time
                      className="text-xs text-muted-foreground whitespace-nowrap shrink-0"
                      title={format(createdAt, "yyyy-MM-dd HH:mm:ss")}
                    >
                      {formatDistanceToNow(createdAt, { addSuffix: true })}
                    </time>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
      <AlertDialog open={resetDemoDialogOpen} onOpenChange={setResetDemoDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reset Demo Account</AlertDialogTitle>
            <AlertDialogDescription>
              Reset <span className="font-semibold">demo@estateplanner.ca</span> to factory state? All estate plan data will be cleared.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => resetDemoMutation.mutate()}
              disabled={resetDemoMutation.isPending}
            >
              {resetDemoMutation.isPending ? "Resetting..." : "Reset Demo"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={testDeathDialogOpen} onOpenChange={setTestDeathDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Create Test Death Event</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="test-death-client">Client</Label>
              <Select value={testDeathEmail} onValueChange={setTestDeathEmail}>
                <SelectTrigger id="test-death-client">
                  <SelectValue placeholder="Select a client…" />
                </SelectTrigger>
                <SelectContent>
                  {allClients.map((c) => (
                    <SelectItem key={c.email} value={c.email}>
                      {c.name} ({c.email})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="test-death-date">Date of Death</Label>
              <Input
                id="test-death-date"
                type="date"
                value={testDeathDate}
                onChange={(e) => setTestDeathDate(e.target.value)}
              />
            </div>
            <p className="text-xs text-muted-foreground">
              This creates a verified death event and upgrades all tier-1 executors for this client to tier-2. Use "Clear Test Events" to remove it.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTestDeathDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={() => createTestDeathMutation.mutate({ clientEmail: testDeathEmail, dateOfDeath: testDeathDate })}
              disabled={!testDeathEmail || createTestDeathMutation.isPending}
            >
              {createTestDeathMutation.isPending ? "Creating…" : "Create Death Event"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
